import { useSyncExternalStore } from 'react';
import type { OneOffChannel } from 'fym-shared/src/music24/oneOffPublish';

/**
 * What the publish dialog shows: one kit asset version on one one-off channel. Any card, the
 * Studio and the "ready" toast open it; `PublishHost` (in `SMLContainer`) draws it. A small store
 * outside React, so a card deep in a list needs no context or prop chain.
 */
export interface PublishDialogRequest {
  accountId: number;
  kitAssetUuid: string;
  catalogItemUuid: string;
  channel: OneOffChannel;
  /** The name of the asset ("Lyric video"), for the "ready" line. */
  assetName: string;
  /** True when the dialog opens because the video just turned ready (the "ready" line shows). */
  justReady?: boolean;
  /** The ready file, for the Download button of a manual channel. */
  downloadUrl?: string | null;
  /**
   * A manual channel of a Kit slot with several files (the four YouTube thumbnails): the dialog
   * lists each file with its own mark, so "I set it" never marks every file blindly. Absent or one
   * file: the dialog acts on `kitAssetUuid` alone.
   */
  files?: PublishDialogFile[];
}

/** One file of a multi-file manual mark. */
export interface PublishDialogFile {
  kitAssetUuid: string;
  /** The asset type and output: the record of the file is the newest record of that type. */
  themeId: string;
  variant: string;
  /** "Lyric video", "Square": the name of the output. */
  label: string;
  downloadUrl: string | null;
}

let current: PublishDialogRequest | null = null;
const listeners = new Set<() => void>();

function emit(): void {
  listeners.forEach((listener) => listener());
}

export function openPublishDialog(request: PublishDialogRequest): void {
  current = request;
  emit();
}

export function closePublishDialog(): void {
  current = null;
  emit();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function usePublishDialogRequest(): PublishDialogRequest | null {
  return useSyncExternalStore(
    subscribe,
    () => current,
    () => null
  );
}

/**
 * The full-song videos that the user made in this tab with the Studio switch off: when one turns
 * ready, the "Publish to YouTube?" toast shows (owner decision 2026-10-09). Kept in
 * `sessionStorage`, so a reload keeps the watch; every access is guarded (a private window may
 * refuse storage).
 */
export interface PublishWatch {
  accountId: number;
  kitAssetUuid: string;
  catalogItemUuid: string;
  themeId: string;
  /** When the asset was made (ms); a watch ends after a day. */
  at: number;
}

const WATCH_KEY = 'm24PublishWatch';
const WATCH_TTL_MS = 24 * 60 * 60 * 1000;

export function readPublishWatches(now: number = Date.now()): PublishWatch[] {
  try {
    const raw = window.sessionStorage.getItem(WATCH_KEY);
    const parsed: unknown = raw === null ? [] : JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter(
      (one): one is PublishWatch =>
        typeof one === 'object' &&
        one !== null &&
        typeof one.kitAssetUuid === 'string' &&
        typeof one.accountId === 'number' &&
        typeof one.at === 'number' &&
        now - one.at < WATCH_TTL_MS
    );
  } catch {
    return [];
  }
}

function writePublishWatches(watches: PublishWatch[]): void {
  try {
    window.sessionStorage.setItem(WATCH_KEY, JSON.stringify(watches));
  } catch {
    // No storage: the toast does not show; the card badge still offers "Publish to YouTube".
  }
}

export function addPublishWatches(watches: PublishWatch[]): void {
  const kept = readPublishWatches().filter((one) => !watches.some((add) => add.kitAssetUuid === one.kitAssetUuid));
  writePublishWatches([...kept, ...watches]);
}

export function removePublishWatch(kitAssetUuid: string): void {
  writePublishWatches(readPublishWatches().filter((one) => one.kitAssetUuid !== kitAssetUuid));
}
